<template>
  <div class="surveys-admin-page">
    <!-- Important Notice -->
    <div class="alert alert-warning d-flex align-items-center mb-4">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      <div>
        <strong>IMPORTANT:</strong> the submission button is only enabled if <strong>all</strong>
        required questions were answered.
      </div>
    </div>

    <div class="card">
      <div class="card-header d-flex align-items-center justify-content-between">
        <h4 class="card-title mb-0">
          <i class="bi bi-clipboard-data me-2"></i>Surveys I'm Invited To
        </h4>
      </div>

      <div class="card-body">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- No surveys message -->
        <div v-else-if="surveys.length === 0" class="text-center py-4">
          <div class="text-muted">
            <i class="bi bi-inbox display-4 d-block mb-3"></i>
            <h5>No survey invitations yet</h5>
            <p>You haven't been invited to any surveys at this time.</p>
          </div>
        </div>

        <!-- Table Controls -->
        <div v-else class="d-flex justify-content-between align-items-center mb-3">
          <div class="d-flex align-items-center">
            <label class="me-2">Show</label>
            <select v-model="entriesPerPage" class="form-select form-select-sm me-2" style="width: auto;">
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span>entries</span>
          </div>
          <div class="d-flex align-items-center">
            <label class="me-2">Search:</label>
            <input
              v-model="searchQuery"
              type="text"
              class="form-control form-control-sm"
              placeholder="Search surveys..."
              style="width: 200px;"
            />
          </div>
        </div>

        <!-- Surveys Table -->
        <div v-if="!loading && surveys.length > 0" class="table-responsive">
          <table class="table table-hover">
            <thead>
            <tr>
              <th>SURVEY TITLE</th>
              <th>DESCRIPTION</th>
              <th>STATUS</th>
              <th>SUBMITTED DATE</th>
              <th>ACTIONS</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="survey in paginatedSurveys" :key="survey.id">
              <td>
                <strong>{{ survey.title }}</strong>
              </td>
              <td>
                <span class="text-muted">{{
                    survey.description || 'No description provided'
                  }}</span>
              </td>
              <td>
                <span class="badge" :class="getStatusClass(survey.status)">
                  {{ survey.status }}
                </span>
              </td>
              <td>
                <span v-if="survey.submitted_at" class="text-muted">
                  {{ formatDate(survey.submitted_at) }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <div class="btn-group">
                  <button
                    class="btn btn-sm btn-outline-secondary dropdown-toggle"
                    type="button"
                    @click="toggleDropdown(survey.id)"
                    :class="{ 'show': openDropdown === survey.id }"
                  >
                    <i class="bi bi-three-dots"></i>
                  </button>
                  <ul
                    class="dropdown-menu"
                    :class="{ 'show': openDropdown === survey.id }"
                    @click="closeDropdown"
                  >
                    <li v-if="survey.status === 'Unread'">
                      <button class="dropdown-item" @click="acceptAndOpen(survey)">
                        <i class="bi bi-envelope-open me-2"></i>Accept and Open
                      </button>
                    </li>
                    <li v-if="survey.status === 'In Progress'">
                      <button class="dropdown-item" @click="continueSurvey(survey)">
                        <i class="bi bi-play-circle me-2"></i>Continue Survey
                      </button>
                    </li>
                    <li v-if="survey.status === 'In Progress'">
                      <button class="dropdown-item" @click="submitSurvey(survey)">
                        <i class="bi bi-check-circle me-2"></i>Submit Survey
                      </button>
                    </li>
                    <li v-if="survey.status === 'Completed'">
                      <button class="dropdown-item" @click="viewSubmission(survey)">
                        <i class="bi bi-eye me-2"></i>View Submission
                      </button>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="!loading && filteredSurveys.length > 0"
             class="d-flex justify-content-between align-items-center mt-3">
          <div class="text-muted">
            Showing {{ startEntry }} to {{ endEntry }} of {{ filteredSurveys.length }} entries
          </div>
          <nav aria-label="Table navigation">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="currentPage = Math.max(1, currentPage - 1)">
                  Previous
                </button>
              </li>
              <li
                v-for="page in totalPages"
                :key="page"
                class="page-item"
                :class="{ active: currentPage === page }"
              >
                <button class="page-link" @click="currentPage = page">
                  {{ page }}
                </button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link"
                        @click="currentPage = Math.min(totalPages, currentPage + 1)">
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Survey Questions Dialog -->
    <div
      v-if="showSurveyDialog && selectedSurvey"
      class="modal fade show"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      @click.self="closeSurveyDialog"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-clipboard-data me-2"></i>
              {{ selectedSurvey.status === 'Completed' ? 'Viewing Survey' : 'Answering Survey' }} -
              {{ surveyData?.title }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeSurveyDialog"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="surveyLoading" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading survey...</span>
              </div>
            </div>
            <div v-else-if="surveyData">
              <div class="survey-subtitle text-muted mb-4">
                {{ surveyData.description }}
              </div>
              <form @submit.prevent="saveSurveyAnswers">
                <div
                  v-for="question in surveyData.questions"
                  :key="question.id"
                  class="mb-4"
                >
                  <label class="form-label fw-semibold">
                    {{ question.text }}
                    <span v-if="question.required" class="text-danger">*</span>
                    <span v-if="question.required" class="text-muted"> (Question required)</span>
                  </label>

                  <!-- Textarea questions -->
                  <div v-if="question.type === 'textarea' || question.type === 'multiple_line_text'">
                    <textarea
                      :value="surveyAnswers[question.id.toString()] || ''"
                      @input="updateAnswer(question.id, $event.target.value)"
                      class="form-control"
                      rows="4"
                      :placeholder="question.placeholder || 'Enter your answer...'"
                      :readonly="selectedSurvey.status === 'Completed'"
                    ></textarea>
                  </div>

                  <!-- Text input questions -->
                  <div v-else-if="question.type === 'text' || question.type === 'single_line_text'">
                    <input
                      :value="surveyAnswers[question.id.toString()] || ''"
                      @input="updateAnswer(question.id, $event.target.value)"
                      type="text"
                      class="form-control"
                      :placeholder="question.placeholder || 'Enter your answer...'"
                      :readonly="selectedSurvey.status === 'Completed'"
                    />
                  </div>

                  <!-- Radio questions -->
                  <div v-else-if="question.type === 'radio' || question.type === 'multiple_choice_single'">
                    <div
                      v-for="option in question.options"
                      :key="option"
                      class="form-check"
                    >
                      <input
                        :id="`q${question.id}_${option}`"
                        :checked="surveyAnswers[question.id.toString()] === option"
                        @change="updateAnswer(question.id, option)"
                        class="form-check-input"
                        type="radio"
                        :name="`question_${question.id}`"
                        :disabled="selectedSurvey.status === 'Completed'"
                      />
                      <label class="form-check-label" :for="`q${question.id}_${option}`">
                        {{ option }}
                      </label>
                    </div>
                  </div>

                  <!-- Checkbox questions -->
                  <div v-else-if="question.type === 'checkbox' || question.type === 'multiple_choice_multiple'">
                    <div
                      v-for="option in question.options"
                      :key="option"
                      class="form-check"
                    >
                      <input
                        :id="`q${question.id}_${option}`"
                        :checked="Array.isArray(surveyAnswers[question.id.toString()]) && surveyAnswers[question.id.toString()].includes(option)"
                        @change="updateCheckboxAnswer(question.id, option, $event.target.checked)"
                        class="form-check-input"
                        type="checkbox"
                        :disabled="selectedSurvey.status === 'Completed'"
                      />
                      <label class="form-check-label" :for="`q${question.id}_${option}`">
                        {{ option }}
                      </label>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div class="modal-footer" v-if="selectedSurvey.status !== 'Completed'">
            <button type="button" class="btn btn-secondary" @click="closeSurveyDialog">
              Cancel
            </button>
            <button type="button" class="btn btn-primary" @click="saveSurveyAnswers" :disabled="surveyLoading || saving">
              <i class="bi bi-save me-2"></i>
              {{ saving ? 'Saving...' : 'Save Answers' }}
            </button>
          </div>
          <div class="modal-footer" v-else>
            <button type="button" class="btn btn-secondary" @click="closeSurveyDialog">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit Survey Confirmation Dialog -->
    <div
      v-if="showSubmitDialog"
      class="modal fade show"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      @click.self="showSubmitDialog = false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-question-circle text-primary me-2"></i>
              Confirm Survey Submission
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="showSubmitDialog = false"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">
              Do you want to submit this survey? Once submitted, you will no longer be able to edit
              your answers.
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showSubmitDialog = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="confirmSubmit"
              :disabled="submitting"
            >
              {{ submitting ? 'Submitting...' : 'Submit Survey' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Toast Notification -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
      <div class="toast" :class="{ show: toast.show, 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error' }" role="alert">
        <div class="toast-header" :class="{ 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error', 'text-white': true }">
          <i class="bi me-2" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
          <strong class="me-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
        </div>
        <div class="toast-body text-white">
          {{ toast.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import SurveyService from '@/services/surveys/SurveyService'

export default {
  name: 'MySurveys',
  data() {
    return {
      toast: {
        show: false,
        message: '',
        type: 'success'
      }
    }
  },

  setup() {
    // Reactive data
    const surveys = ref([])
    const loading = ref(false)
    const searchQuery = ref('')
    const entriesPerPage = ref(10)
    const currentPage = ref(1)
    const showSubmitDialog = ref(false)
    const showSurveyDialog = ref(false)
    const selectedSurvey = ref(null)
    const surveyData = ref(null)
    const surveyLoading = ref(false)
    const saving = ref(false)
    const submitting = ref(false)
    const surveyAnswers = reactive({})
    const existingAnswers = ref({})
    const answerId = ref(null)
    const openDropdown = ref(null)

    // Computed property to check if all required fields are filled
    const canSubmit = computed(() => {
      if (!surveyData.value || !surveyData.value.questions) return false

      const requiredQuestions = surveyData.value.questions.filter(q => q.required === true || q.required === 'true')

      for (const question of requiredQuestions) {
        const questionKey = question.id.toString()
        const answer = surveyAnswers[questionKey]

        if (!answer || (typeof answer === 'string' && answer.trim() === '') ||
          (Array.isArray(answer) && answer.length === 0)) {
          return false
        }
      }

      return true
    })

    return {
      surveys,
      loading,
      searchQuery,
      entriesPerPage,
      currentPage,
      showSubmitDialog,
      showSurveyDialog,
      selectedSurvey,
      surveyData,
      surveyLoading,
      saving,
      submitting,
      surveyAnswers,
      existingAnswers,
      answerId,
      openDropdown,
      canSubmit,
      // nextTick
    }
  },

  computed: {
    filteredSurveys() {
      if (!this.searchQuery) {
        return this.surveys
      }
      return this.surveys.filter(survey =>
        survey.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        survey.status.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (survey.description && survey.description.toLowerCase().includes(this.searchQuery.toLowerCase()))
      )
    },
    totalPages() {
      return Math.ceil(this.filteredSurveys.length / this.entriesPerPage)
    },
    paginatedSurveys() {
      const start = (this.currentPage - 1) * this.entriesPerPage
      const end = start + this.entriesPerPage
      return this.filteredSurveys.slice(start, end)
    },
    startEntry() {
      if (this.filteredSurveys.length === 0) return 0
      return (this.currentPage - 1) * this.entriesPerPage + 1
    },
    endEntry() {
      return Math.min(this.currentPage * this.entriesPerPage, this.filteredSurveys.length)
    }
  },

  async mounted() {
    await this.loadMySurveys()
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    showToast(type, message) {
      this.toast = {
        show: true,
        type: type,
        message: message
      }
      setTimeout(() => {
        this.toast.show = false
      }, 3000)
    },

    updateAnswer(questionId, value) {
      const key = questionId.toString()
      this.surveyAnswers[key] = value
    },

    updateCheckboxAnswer(questionId, option, checked) {
      const key = questionId.toString()

      if (!Array.isArray(this.surveyAnswers[key])) {
        this.surveyAnswers[key] = []
      }

      if (checked) {
        if (!this.surveyAnswers[key].includes(option)) {
          this.surveyAnswers[key].push(option)
        }
      } else {
        const index = this.surveyAnswers[key].indexOf(option)
        if (index > -1) {
          this.surveyAnswers[key].splice(index, 1)
        }
      }
    },

    initializeSurveyAnswers(questions, existingAnswers = {}) {
      // Clear existing answers but keep the reactive object
      Object.keys(this.surveyAnswers).forEach(key => {
        delete this.surveyAnswers[key]
      })

      // Initialize each question
      questions.forEach(question => {
        const key = question.id.toString()
        if (question.type === 'checkbox' || question.type === 'multiple_choice_multiple') {
          this.surveyAnswers[key] = []
        } else {
          this.surveyAnswers[key] = ''
        }
      })

      // Load existing answers
      Object.keys(existingAnswers).forEach(questionKey => {
        const answer = existingAnswers[questionKey]
        const answerText = answer.answer_text || answer.answer_key || ''
        if (answerText) {
          const question = questions.find(q => q.id.toString() === questionKey)
          if (question && (question.type === 'checkbox' || question.type === 'multiple_choice_multiple')) {
            this.surveyAnswers[questionKey] = answerText.includes(',')
              ? answerText.split(', ').filter(item => item.trim() !== '')
              : [answerText]
          } else {
            this.surveyAnswers[questionKey] = answerText
          }
        }
      })
    },

    handleClickOutside(event) {
      if (!event.target.closest('.btn-group')) {
        this.openDropdown = null
      }
    },

    async loadMySurveys() {
      try {
        this.loading = true
        const response = await SurveyService.getMySurveys()

        if (response && response.success && response.data) {
          this.surveys = Array.isArray(response.data) ? response.data : []
        } else if (response && response.Data && response.Data.success && response.Data.data) {
          this.surveys = Array.isArray(response.Data.data) ? response.Data.data : []
        } else if (response && Array.isArray(response)) {
          this.surveys = response
        } else {
          const errorMessage = response?.message || response?.Data?.message || 'Unknown error occurred'
          this.showToast('error', `Failed to load survey invitations: ${errorMessage}`)
          this.surveys = []
        }
      } catch (error) {
        console.error('Error loading survey invitations:', error)
        this.showToast('error', 'Error loading survey invitations')
        this.surveys = []
      } finally {
        this.loading = false
      }
    },

    toggleDropdown(surveyId) {
      this.openDropdown = this.openDropdown === surveyId ? null : surveyId
    },

    closeDropdown() {
      this.openDropdown = null
    },

    getStatusClass(status) {
      switch (status) {
        case 'In Progress':
          return 'bg-warning text-dark'
        case 'Unread':
          return 'bg-info'
        case 'Completed':
          return 'bg-success'
        default:
          return 'bg-secondary'
      }
    },

    async acceptAndOpen(survey) {
      try {
        this.showToast('info', 'Opening survey...')
        const response = await SurveyService.acceptAndOpenSurvey(survey.id)

        let success = false
        let message = null
        if (response && response.Data) {
          success = response.Data.success
          message = response.Data.message || response.Message
        } else if (response && response.success !== undefined) {
          success = response.success
          message = response.message
        }

        if (success) {
          this.showToast('success', 'Survey opened successfully!')
          await this.loadMySurveys()
          setTimeout(() => {
            const updatedSurvey = this.surveys.find(s => s.id === survey.id) || survey
            this.continueSurvey(updatedSurvey)
          }, 500)
        } else {
          const errorMsg = message || 'Failed to open survey'
          this.showToast('error', errorMsg)
        }
      } catch (error) {
        console.error('Error accepting survey:', error)
        this.showToast('error', 'Error opening survey: ' + (error.message || 'Unknown error'))
      }
    },

    async continueSurvey(survey) {
      try {
        this.selectedSurvey = survey
        this.surveyLoading = true
        this.showSurveyDialog = true
        // await this.nextTick()

        const response = await SurveyService.continueSurvey(survey.id)

        let responseData = null
        let success = false
        if (response && response.Data) {
          success = response.Data.success
          responseData = response.Data.data
        } else if (response && response.success !== undefined) {
          success = response.success
          responseData = response.data
        }

        if (success && responseData) {
          const surveyData = responseData.survey
          this.surveyData = {
            id: surveyData.id,
            title: surveyData.title,
            description: surveyData.description,
            questions: surveyData.structure && surveyData.structure.questions
              ? SurveyService.formatSurveyStructure(surveyData.structure).questions
              : []
          }

          this.existingAnswers = responseData.existing_answers || {}
          this.answerId = responseData.answer_id

          this.initializeSurveyAnswers(this.surveyData.questions, this.existingAnswers)
        } else {
          const errorMsg = response?.message || response?.Data?.message || 'Failed to load survey'
          this.showToast('error', errorMsg)
          this.closeSurveyDialog()
        }
      } catch (error) {
        console.error('Error loading survey:', error)
        this.showToast('error', 'Error loading survey: ' + (error.message || 'Unknown error'))
        this.closeSurveyDialog()
      } finally {
        this.surveyLoading = false
      }
    },

    async submitSurvey(survey) {
      // First load the survey if not already loaded
      if (!this.surveyData || this.selectedSurvey?.id !== survey.id) {
        await this.continueSurvey(survey)
        // Wait a moment for the form to load
        await new Promise(resolve => setTimeout(resolve, 10))
      }

      // Check if we have answers now
      if (!this.surveyAnswers || Object.keys(this.surveyAnswers).length === 0) {
        this.showToast('error', 'Please fill out the survey form before submitting')
        return
      }

      // Show the submit dialog
      this.showSubmitDialog = true
    },

    async submitSurveyFromDialog() {
      if (!this.selectedSurvey || !this.surveyData) return

      try {
        this.submitting = true

        // Format answers correctly
        const formattedAnswers = {}
        const requiredQuestions = this.surveyData.questions.filter(q => q.required === true || q.required === 'true')
        let missingRequiredFields = []

        this.surveyData.questions.forEach(question => {
          const questionKey = question.id.toString()
          const answer = this.surveyAnswers[questionKey]

          const isRequired = question.required === true || question.required === 'true'
          let hasAnswer = false

          if (answer !== null && answer !== undefined) {
            if (typeof answer === 'string') {
              hasAnswer = answer.trim() !== ''
            } else if (Array.isArray(answer)) {
              hasAnswer = answer.length > 0 && answer.some(item => item && item.toString().trim() !== '')
            } else {
              hasAnswer = answer.toString().trim() !== ''
            }
          }

          if (isRequired && !hasAnswer) {
            missingRequiredFields.push(question.text)
          } else if (hasAnswer) {
            let finalAnswer = answer
            if (Array.isArray(answer)) {
              finalAnswer = answer.join(', ')
            } else {
              finalAnswer = answer.toString()
            }
            formattedAnswers[questionKey] = {
              answer_key: finalAnswer,
              answer_text: finalAnswer
            }
          }
        })

        // Check for missing required fields
        if (missingRequiredFields.length > 0) {
          this.showToast('error', `Please complete these required fields: ${missingRequiredFields.join(', ')}`)
          return
        }

        // Check if we have any answers at all
        if (Object.keys(formattedAnswers).length === 0) {
          this.showToast('error', 'Please answer at least one question before submitting')
          return
        }

        // Submit the survey
        const response = await SurveyService.submitSurvey(this.selectedSurvey.id, formattedAnswers)

        let success = false
        let message = ''
        if (response && response.Data) {
          success = response.Data.success
          message = response.Data.message || response.Message || ''
        } else if (response && response.success !== undefined) {
          success = response.success
          message = response.message || ''
        }

        if (success) {
          this.showToast('success', 'Survey submitted successfully!')
          this.showSubmitDialog = false
          this.closeSurveyDialog()
          await this.loadMySurveys()
        } else {
          this.showToast('error', message || 'Failed to submit survey')
        }
      } catch (error) {
        console.error('Error submitting survey:', error)
        this.showToast('error', 'Error submitting survey: ' + (error.message || 'Unknown error'))
      } finally {
        this.submitting = false
      }
    },

    async confirmSubmit() {
      await this.submitSurveyFromDialog()
    },

    async viewSubmission(survey) {
      await this.continueSurvey(survey)
    },

    async saveSurveyAnswers() {
      if (!this.selectedSurvey || !this.surveyData) return

      try {
        this.saving = true
        const formattedAnswers = SurveyService.formatAnswersForApi(this.surveyAnswers)
        const response = await SurveyService.saveAnswers(this.selectedSurvey.id, formattedAnswers)

        console.log('Reponse: ', response)

        let success = false
        let message = null
        if (response && response.Data) {
          success = response.Data.success
          message = response.Data.message || response.Message
        } else if (response && response.success !== undefined) {
          success = response.success
          message = response.message
        }

        if (success) {
          this.showToast('success', 'Survey answers saved successfully!')
          await this.loadMySurveys()
        } else {
          this.showToast('error', message || 'Failed to save answers')
        }
      } catch (error) {
        console.error('Error saving answers:', error)
        this.showToast('error', 'Error saving answers')
      } finally {
        this.saving = false
      }
    },

    closeSurveyDialog() {
      this.showSurveyDialog = false
      this.selectedSurvey = null
      this.surveyData = null
      // Clear answers but keep the reactive object
      Object.keys(this.surveyAnswers).forEach(key => {
        delete this.surveyAnswers[key]
      })
      this.existingAnswers = {}
      this.answerId = null
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },

  watch: {
    searchQuery() {
      this.currentPage = 1
    },
    entriesPerPage() {
      this.currentPage = 1
    }
  }
}
</script>

<style scoped>
.surveys-admin-page {
  padding: 1rem 0;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
}

.table th {
  font-weight: 600;
  color: #6c757d;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

.dropdown-toggle::after {
  display: none;
}

.dropdown-item {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.pagination .page-link {
  color: #6c757d;
  border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
  background-color: #009CAD;
  border-color: #009CAD;
}

.pagination .page-link:hover {
  color: #009CAD;
  background-color: #e9ecef;
}

/* Modal Styles */
.modal-content {
  border-radius: 8px;
  border: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  overflow-y: auto;
}

.btn-group {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  display: none;
  min-width: 10rem;
  padding: 0.5rem 0;
  margin: 0;
  font-size: 0.875rem;
  color: #212529;
  text-align: left;
  list-style: none;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
}

.dropdown-menu.show {
  display: block;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 0.375rem 1rem;
  clear: both;
  font-weight: 400;
  color: #212529;
  text-align: inherit;
  text-decoration: none;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
}

.dropdown-item:hover,
.dropdown-item:focus {
  color: #1e2125;
  background-color: #e9ecef;
}

.modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
}

.modal-title {
  color: #2c3e50;
  font-weight: 600;
  font-size: 1rem;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  opacity: 0.6;
}

.btn-close:hover {
  opacity: 1;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #004085;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.btn-success:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  opacity: 0.65;
}

/* Survey Dialog Styles */
.modal-lg {
  max-width: 800px;
}

.survey-subtitle {
  font-size: 0.9rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.form-label {
  margin-bottom: 0.75rem;
  color: #495057;
}

.form-check {
  margin-bottom: 0.5rem;
}

.form-check-input {
  margin-top: 0.25rem;
}

.form-check-label {
  margin-left: 0.5rem;
  color: #6c757d;
}

.text-danger {
  color: #dc3545 !important;
}

textarea.form-control {
  resize: vertical;
  min-height: 100px;
}

textarea.form-control:focus {
  border-color: #009CAD;
  box-shadow: 0 0 0 0.2rem rgba(0, 156, 173, 0.25);
}

.spinner-border {
  color: #009CAD;
}

.display-4 {
  font-size: 3rem;
  opacity: 0.3;
}

.form-control[readonly] {
  background-color: #f8f9fa;
  opacity: 1;
}

.form-check-input[disabled] {
  opacity: 0.7;
}
</style>
